import { BoundingBox } from '../../../../../db/BoundingBox.js';
import { Point } from '../../../../../db/Point.js';
import { type Exam } from '../../../../../db/exam.js';
import { type BoundingBoxContentType } from '../../../../../shared/bounding-box-content.js';
import { STRUCTURE_EDITOR_BBX_THRESHOLDS } from '../../../../lib/constants.js';

export const DEFAULT_ZOOM = 1.0;
export const FALLBACK_PAGE_ASPECT_RATIO = 800 / 1131;
export const DEFAULT_CARD_HEIGHT_PX = 124;
export const MIN_CARD_GAP_PX = 12;
export const PAGE_RAIL_GAP_PX = 10;

export interface PercentBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  ocrExtractedText: string;
  contentType?: BoundingBoxContentType;
  imageId?: string | null;
  confidence?: number;
  approved?: boolean;
}

export interface StructureEditorTask {
  id: string;
  path: string[];
  taskType?: string[];
  description: string;
  masterSolutionContent?: string;
  gradingCriteria?: string;
  /**
   * The DECLARED total for this task (ExamTask.declaredMaxPoints; for a leaf, falls back to its grading
   * scheme). A leaf → its own points; a task with children → its declared TOTAL. Persisted to
   * `declaredMaxPoints`.
   */
  declaredMaxPoints?: number;
  /**
   * An answerable parent's OWN points (its own-answer worth, distinct from the total). Editable, optional.
   * Persisted into the task's `gradingScheme.maxPoints` on save (and read back from there on load); the
   * total = own + children. When set, `own + children` must equal the declared total.
   */
  ownPoints?: number;
  /** `false` marks an aggregator (non-answerable) task whose points must equal the sum of its children. */
  needsAnswer?: boolean;
  boundingBoxes: PercentBox[];
  additionalInfoIds?: string[];
}

export interface StructureEditorAdditionalInfo {
  id: string;
  content: string;
  boundingBoxes: PercentBox[];
}

export type PageBoxItem =
  | { kind: 'task'; task: StructureEditorTask; box: PercentBox; boxKey: string }
  | {
      kind: 'info';
      info: StructureEditorAdditionalInfo;
      box: PercentBox;
      boxKey: string;
      referencedTaskPaths: string[][];
    };

export interface SelectionAnchor {
  path: string[];
  boxIndex: number;
}

export function generateUuid(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function aggregateBoxText(boxes: PercentBox[]): string {
  if (!Array.isArray(boxes) || boxes.length === 0) return '';
  return boxes
    .map(box => String(box.ocrExtractedText ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

export function uniqueStrings(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value?.trim()))));
}

export function toPercentBox(box: any): PercentBox | null {
  const topLeft = box?.topLeft;
  const bottomRight = box?.bottomRight;
  if (typeof topLeft?.x !== 'number' || typeof topLeft?.y !== 'number') return null;
  if (typeof bottomRight?.x !== 'number' || typeof bottomRight?.y !== 'number') return null;

  const x1 = topLeft.x / 10;
  const y1 = topLeft.y / 10;
  const x2 = bottomRight.x / 10;
  const y2 = bottomRight.y / 10;
  const width = Math.max(1, x2 - x1);
  const height = Math.max(1, y2 - y1);

  return {
    id: String(box._id ?? generateUuid()),
    x: Math.max(0, Math.min(x1, 99)),
    y: Math.max(0, Math.min(y1, 99)),
    width: Math.max(1, Math.min(width, 100)),
    height: Math.max(1, Math.min(height, 100)),
    pageNumber: Math.max(0, Number(box.pageNumber ?? 1) - 1),
    ocrExtractedText: String(box.ocrExtractedText ?? ''),
    contentType: box.contentType ?? undefined,
    imageId: box.imageId != null ? String(box.imageId) : null,
    confidence: typeof box.confidence === 'number' ? box.confidence : undefined,
    approved: box?.approved === true,
  };
}

export function toBoundingBoxEntity(box: PercentBox): BoundingBox {
  return new BoundingBox({
    _id: box.id,
    pageNumber: box.pageNumber + 1,
    imageId: box.imageId ?? null,
    ocrExtractedText: box.ocrExtractedText,
    contentType: box.contentType,
    confidence: box.confidence,
    approved: box.approved === true,
    isHandwritten: false,
    topLeft: new Point({ x: box.x * 10, y: box.y * 10 }),
    bottomRight: new Point({ x: (box.x + box.width) * 10, y: (box.y + box.height) * 10 }),
  });
}

export function flattenTasks(exam: Exam | null): StructureEditorTask[] {
  const tasks: StructureEditorTask[] = [];
  const sourceTasks = Array.isArray(exam?.tasks) ? (exam?.tasks as any[]) : [];

  const unwrapDoc = <T>(value: T | { doc?: T } | undefined): T | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object' && value !== null && 'doc' in (value as any)) {
      return (value as any).doc as T | undefined;
    }
    return value as T;
  };

  const traverse = (node: any, ancestors: string[], parentId: string | null) => {
    const nodeNumber = String(node?.taskNumber ?? '').trim();
    const path = [...ancestors, nodeNumber].filter(Boolean);
    const boxes = Array.isArray(node?.boundingBoxes)
      ? node.boundingBoxes.map((box: any) => toPercentBox(box)).filter(Boolean)
      : [];
    const nodeId = String(
      node?._id ??
        (parentId ? `${parentId}-${nodeNumber || tasks.length + 1}` : `main-${nodeNumber || tasks.length + 1}`),
    );
    const masterSolution = unwrapDoc<any>(node?.masterSolution);
    const gradingScheme = unwrapDoc<any>(node?.gradingScheme);

    const children = Array.isArray(node?.subTasks) ? node.subTasks : [];
    const isAnswerable = node?.needsAnswer !== false;
    const hasKids = children.length > 0;
    // 0 means "no points assigned" — treat a 0 scheme/total as unset (own = 0 → no own points).
    const schemePts =
      typeof gradingScheme?.maxPoints === 'number' && gradingScheme.maxPoints > 0 ? gradingScheme.maxPoints : undefined;

    tasks.push({
      id: nodeId,
      path,
      taskType: Array.isArray(node?.taskType)
        ? uniqueStrings(node.taskType)
        : typeof node?.taskType === 'string' && node.taskType.trim().length > 0
          ? [node.taskType]
          : undefined,
      description: String(node?.description ?? ''),
      masterSolutionContent: String(masterSolution?.content ?? ''),
      gradingCriteria: String(gradingScheme?.criteria ?? ''),
      // The declared TOTAL for this task. For a leaf without a stored total, fall back to its scheme points.
      // 0 means "no points assigned" → treat as unset.
      declaredMaxPoints:
        typeof node?.declaredMaxPoints === 'number' && node.declaredMaxPoints > 0
          ? node.declaredMaxPoints
          : !hasKids && schemePts != null
            ? schemePts
            : undefined,
      // An answerable parent's OWN points live in its grading scheme (own answer, distinct from the total).
      ownPoints: isAnswerable && hasKids ? schemePts : undefined,
      needsAnswer: isAnswerable,
      boundingBoxes: boxes.length > 0 ? (boxes as PercentBox[]) : [],
      additionalInfoIds: Array.isArray(node?.additionalInfoIds) ? node.additionalInfoIds : [],
    });

    const syntheticTaskTypeChildren =
      node?.taskType === 'TASK_FRAME' &&
      children.length > 0 &&
      children.every((child: any) => {
        const childTaskType = typeof child?.taskType === 'string' ? child.taskType.trim() : '';
        const childBoxes = Array.isArray(child?.boundingBoxes) ? child.boundingBoxes : [];
        return (
          childTaskType.length > 0 &&
          childBoxes.length === 0 &&
          !String(child?.description ?? '').trim() &&
          !String(child?.masterSolutionContent ?? '').trim() &&
          !String(child?.gradingCriteria ?? '').trim() &&
          (!Array.isArray(child?.subTasks) || child.subTasks.length === 0)
        );
      });

    if (syntheticTaskTypeChildren) {
      const mergedTaskTypes = uniqueStrings(
        children.map((child: any) => (typeof child?.taskType === 'string' ? child.taskType : '')).filter(Boolean),
      );
      if (mergedTaskTypes.length > 0) {
        const currentTask = tasks[tasks.length - 1];
        if (currentTask && currentTask.id === nodeId) {
          currentTask.taskType = mergedTaskTypes;
        }
      }
      return;
    }

    for (const child of children) traverse(child, path, nodeId);
  };

  for (const main of sourceTasks) traverse(main, [], null);
  return tasks;
}

export function flattenAdditionalInfo(exam: Exam | null): StructureEditorAdditionalInfo[] {
  const items = Array.isArray(exam?.additionalInformation) ? (exam?.additionalInformation as any[]) : [];
  return items
    .map((item: any) => {
      const id = String(item?._id ?? '');
      if (!id) return null;
      const boxes = Array.isArray(item?.boundingBoxes)
        ? item.boundingBoxes.map((box: any) => toPercentBox(box)).filter(Boolean)
        : [];
      return {
        id,
        content: String(item?.content ?? ''),
        boundingBoxes: boxes as PercentBox[],
      };
    })
    .filter((item): item is StructureEditorAdditionalInfo => item !== null);
}

export function getConfidenceCardClasses(confidence: number | undefined): string {
  if (confidence === undefined) return 'border-slate-200 hover:border-slate-300';
  if (confidence >= STRUCTURE_EDITOR_BBX_THRESHOLDS.HIGH_CONFIDENCE)
    return 'border-green-300 bg-green-50/40 hover:border-green-400';
  if (confidence >= STRUCTURE_EDITOR_BBX_THRESHOLDS.LOW_CONFIDENCE)
    return 'border-amber-300 bg-amber-50/40 hover:border-amber-400';
  return 'border-red-300 bg-red-50/40 hover:border-red-400';
}

export function normalizeBox(box: PercentBox): PercentBox {
  const width = Math.max(1, Math.min(box.width, 100));
  const height = Math.max(1, Math.min(box.height, 100));
  const x = Math.max(0, Math.min(box.x, 100 - width));
  const y = Math.max(0, Math.min(box.y, 100 - height));
  return { ...box, x, y, width, height };
}

export function getNextMainTaskNumber(tasks: StructureEditorTask[]): string {
  const numeric = tasks
    .map(task => Number(String((Array.isArray(task.path) ? task.path[0] : '') ?? '').replace(/[^0-9]/g, '')))
    .filter(value => Number.isFinite(value) && value > 0);
  const next = numeric.length > 0 ? Math.max(...numeric) + 1 : 1;
  return String(next);
}

export function getSelectionAnchor(
  tasks: StructureEditorTask[],
  taskId: string,
  boxKey: string,
): SelectionAnchor | null {
  const task = tasks.find(item => item.id === taskId);
  if (!task) return null;

  const boxIndex = task.boundingBoxes.findIndex(box => `${task.id}-${box.id}` === boxKey);
  return {
    path: Array.isArray(task.path) ? task.path : [],
    boxIndex: boxIndex >= 0 ? boxIndex : 0,
  };
}

export function resolveSelectionAnchor(
  tasks: StructureEditorTask[],
  anchor: SelectionAnchor | null,
): { taskId: string; boxKey: string } | null {
  if (!anchor) return null;

  const task = tasks.find(item => {
    const p = Array.isArray(item.path) ? item.path : [];
    if (p.length !== anchor.path.length) return false;
    for (let i = 0; i < p.length; i++) if (p[i] !== anchor.path[i]) return false;
    return true;
  });
  if (!task) return null;

  const exactBox = task.boundingBoxes.find((box, index) => index === anchor.boxIndex);
  const fallbackBox = task.boundingBoxes[0];
  const box = exactBox ?? fallbackBox;
  if (!box) return null;

  return {
    taskId: task.id,
    boxKey: `${task.id}-${box.id}`,
  };
}

export function mergeTasksPreservingLocalGeometry(
  localTasks: StructureEditorTask[],
  serverTasks: StructureEditorTask[],
): StructureEditorTask[] {
  if (localTasks.length === 0) return serverTasks;

  const localTasksById = new Map(localTasks.map(task => [task.id, task]));

  return serverTasks.map(serverTask => {
    const localTask = localTasksById.get(serverTask.id);
    if (!localTask) return serverTask;

    const localBoxesById = new Map(localTask.boundingBoxes.map(box => [box.id, box]));
    const mergedBoxes = serverTask.boundingBoxes.map(serverBox => {
      const localBox = localBoxesById.get(serverBox.id);
      if (!localBox) return serverBox;

      return {
        ...serverBox,
        x: localBox.x,
        y: localBox.y,
        width: localBox.width,
        height: localBox.height,
      };
    });

    return {
      ...serverTask,
      boundingBoxes: mergedBoxes,
      taskType:
        Array.isArray(localTask.taskType) && localTask.taskType.length > 0 ? localTask.taskType : serverTask.taskType,
      // Prefer local additionalInfoIds — they may contain pending checkbox changes not yet persisted
      additionalInfoIds: localTask.additionalInfoIds ?? serverTask.additionalInfoIds,
      // Keep a pending local own edit across server refreshes (own persists via gradingScheme.maxPoints).
      // Only a POSITIVE local own is kept — a 0 means "no points assigned" (never a real value), so let it
      // fall through to the server (which drops it), otherwise `?? ` would pin a stale local 0 forever.
      ownPoints:
        typeof localTask.ownPoints === 'number' && localTask.ownPoints > 0 ? localTask.ownPoints : serverTask.ownPoints,
    };
  });
}

export function mergeAdditionalInfoPreservingLocalGeometry(
  localItems: StructureEditorAdditionalInfo[],
  serverItems: StructureEditorAdditionalInfo[],
): StructureEditorAdditionalInfo[] {
  if (localItems.length === 0) return serverItems;

  const localById = new Map(localItems.map(item => [item.id, item]));

  return serverItems.map(serverItem => {
    const localItem = localById.get(serverItem.id);
    if (!localItem) return serverItem;

    const localBoxesById = new Map(localItem.boundingBoxes.map(box => [box.id, box]));
    const mergedBoxes = serverItem.boundingBoxes.map(serverBox => {
      const localBox = localBoxesById.get(serverBox.id);
      if (!localBox) return serverBox;
      return { ...serverBox, x: localBox.x, y: localBox.y, width: localBox.width, height: localBox.height };
    });

    return { ...serverItem, boundingBoxes: mergedBoxes };
  });
}

/** A task whose points input is required-but-empty within a partially-assigned level (→ red). */
export interface FlatPointInconsistency {
  taskId: string;
  path: string[];
}

/** Direct children of `path` within the flat task list (path exactly one segment longer). */
function flatChildrenOf(tasks: StructureEditorTask[], path: string[]): StructureEditorTask[] {
  return tasks.filter(candidate => {
    const p = Array.isArray(candidate.path) ? candidate.path : [];
    if (p.length !== path.length + 1) return false;
    for (let i = 0; i < path.length; i++) if (p[i] !== path[i]) return false;
    return true;
  });
}

const flatHasOwnPoints = (task: StructureEditorTask): boolean =>
  typeof task.declaredMaxPoints === 'number' && Number.isFinite(task.declaredMaxPoints);

/**
 * The task's resolved TOTAL, or undefined if no points are assigned anywhere in it yet.
 *  - leaf → its declared points ARE its total.
 *  - task frame → the children sum when any child is pointed, else its declared total (top-down).
 *  - answerable parent → its declared TOTAL is the anchor (own points are the implicit remainder); with
 *    no declared total it falls back to the dynamic children sum.
 */
export function computeTaskTotal(task: StructureEditorTask, tasks: StructureEditorTask[]): number | undefined {
  const children = flatChildrenOf(tasks, task.path);
  const childValues = children.map(child => computeTaskTotal(child, tasks));
  const childSum = childValues.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  const anyChildPointed = childValues.some(value => value !== undefined);
  const declared = flatHasOwnPoints(task) ? (task.declaredMaxPoints as number) : undefined;
  if (children.length === 0) return declared; // leaf: declared points are its total
  // A FIXED declared total is this node's source of truth for the level ABOVE: its parent aggregates THIS
  // value, not the live subtask sum (so a fixed 1.a stays 4 while you fill in 1.a.i). Only a DYNAMIC parent
  // (its declared already dropped by reconcile) reports its own + subtask sum. When the sum matches the
  // declared, reconcile has dropped declared → null → dynamic, so the two agree.
  if (declared != null) return declared;
  const ownSet = task.needsAnswer !== false && typeof task.ownPoints === 'number' && Number.isFinite(task.ownPoints);
  const own = ownSet ? (task.ownPoints as number) : 0;
  const anythingPointed = anyChildPointed || ownSet;
  return anythingPointed ? own + childSum : undefined;
}

/**
 * Per-level point-consistency (mirrors the server's `findPointInconsistencies`). Checks only the NEXT level
 * under each parent (its direct children — leaf OR parent alike):
 *  1. MISSING (all-or-nothing): among a parent's DIRECT children (plus the parent's own answer when set),
 *     once ANY carries points, every one must — the empty ones are flagged red. A child "carries points"
 *     when its subtree total is defined. A level with none pointed is fine (top-down / distributed later);
 *     an unset own is ignored unless something else at the level is pointed.
 *  2. MISMATCH: a parent with a FIXED declared total whose children are ALL pointed but `own + children`
 *     (own = 0 for a task frame / when unset) does not equal the total. (No declared total = dynamic = always
 *     consistent; not-all-pointed = handled by MISSING.)
 */
export function findFlatPointInconsistencies(tasks: StructureEditorTask[]): FlatPointInconsistency[] {
  const out: FlatPointInconsistency[] = [];
  const key = (path: string[]): string => path.join('|');
  const byPath = new Map(tasks.map(task => [key(task.path), task]));
  const childrenByParent = new Map<string, StructureEditorTask[]>();
  for (const task of tasks) {
    const parentKey = key(task.path.slice(0, -1));
    if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
    childrenByParent.get(parentKey)!.push(task);
  }
  const hasPoints = (task: StructureEditorTask): boolean => computeTaskTotal(task, tasks) !== undefined;
  // An answerable parent's OWN answer is just another subtask slot: the slot always exists, and it counts as
  // assigned only when own > 0 (0 = no points assigned).
  const ownPoints = (task: StructureEditorTask | undefined): number =>
    !!task && task.needsAnswer !== false && typeof task.ownPoints === 'number' && task.ownPoints > 0
      ? task.ownPoints
      : 0;

  for (const [parentKey, children] of childrenByParent) {
    if (children.length === 0) continue;
    const parent = byPath.get(parentKey);
    const ownIsSlot = !!parent && parent.needsAnswer !== false; // answerable parent → own is a slot
    const ownAssigned = ownPoints(parent) > 0;
    const totalSlots = children.length + (ownIsSlot ? 1 : 0);
    const assignedCount = children.filter(hasPoints).length + (ownAssigned ? 1 : 0);
    if (assignedCount === 0 || assignedCount === totalSlots) continue; // all-or-nothing → OK
    for (const child of children) {
      if (!hasPoints(child)) out.push({ taskId: child.id, path: Array.isArray(child.path) ? child.path : [] });
    }
    // own is an empty slot among pointed siblings → flag the parent (its own field).
    if (ownIsSlot && !ownAssigned && parent)
      out.push({ taskId: parent.id, path: Array.isArray(parent.path) ? parent.path : [] });
  }

  for (const task of tasks) {
    if (typeof task.declaredMaxPoints !== 'number' || !Number.isFinite(task.declaredMaxPoints)) continue;
    const children = flatChildrenOf(tasks, task.path);
    if (children.length === 0) continue;
    const childValues = children.map(child => computeTaskTotal(child, tasks));
    if (childValues.some(value => value === undefined)) continue; // partial → covered by all-or-nothing
    const childSum = childValues.reduce<number>((acc, value) => acc + (value ?? 0), 0);
    if (Math.abs(ownPoints(task) + childSum - task.declaredMaxPoints) > 0.001) {
      out.push({ taskId: task.id, path: Array.isArray(task.path) ? task.path : [] });
    }
  }
  // A parent can be flagged by both the own-slot check and the mismatch check — dedupe by task.
  const seen = new Set<string>();
  return out.filter(issue => (seen.has(issue.taskId) ? false : (seen.add(issue.taskId), true)));
}

/**
 * QoL fixed↔dynamic cycle for ANY parent (task frame or answerable). When a parent's dynamic sum — own (0
 * for a task frame) + all children — equals its fixed declared total, the declared total is redundant, so
 * drop it and the parent goes DYNAMIC (moves with the sum). `excludeId` (the task the teacher just edited)
 * is never cleared, so editing a total FIXES it even if it currently equals the sum; it only goes dynamic
 * once a CHILD or OWN edit brings the sum to it. A leaf's declared is its own points — never dropped.
 * Idempotent.
 */
export function reconcileMatchedTaskFrames(tasks: StructureEditorTask[], excludeId?: string): StructureEditorTask[] {
  return tasks.map(task => {
    if (task.id === excludeId || typeof task.declaredMaxPoints !== 'number') return task;
    const children = flatChildrenOf(tasks, task.path);
    if (children.length === 0) return task; // leaf: declared is its own points
    // Go dynamic as soon as own + the CURRENT subtask sum equals the declared total — regardless of whether
    // every child is assigned yet (unassigned children count as 0). Then the total tracks the sum.
    const childValues = children.map(child => computeTaskTotal(child, tasks));
    const childSum = childValues.reduce<number>((acc, value) => acc + (value ?? 0), 0);
    const own =
      task.needsAnswer !== false && typeof task.ownPoints === 'number' && Number.isFinite(task.ownPoints)
        ? task.ownPoints
        : 0;
    if (Math.abs(own + childSum - task.declaredMaxPoints) < 0.001) return { ...task, declaredMaxPoints: undefined };
    return task;
  });
}

function normalizeTaskSegment(s: string): string {
  return s.replace(/[.)]+$/, '');
}

function normalizeTaskTypes(taskType: string[] | undefined): string[] {
  return uniqueStrings(Array.isArray(taskType) ? taskType : []);
}

export function serializeStructureEditorTasks(tasks: StructureEditorTask[]): any[] {
  const nodeMap = new Map<string, any>();
  const normalizedNodeMap = new Map<string, any>();

  const ensureNode = (path: string[]): any => {
    const exactKey = path.join('|');
    const normKey = path.map(normalizeTaskSegment).join('|');

    if (nodeMap.has(exactKey)) return nodeMap.get(exactKey);
    if (normalizedNodeMap.has(normKey)) return normalizedNodeMap.get(normKey);

    const node: any = {
      taskNumber: path[path.length - 1] ?? '',
      subtasks: [],
    };
    nodeMap.set(exactKey, node);
    normalizedNodeMap.set(normKey, node);

    if (path.length > 1) {
      const parent = ensureNode(path.slice(0, -1));
      const normTaskNumber = normalizeTaskSegment(node.taskNumber);
      if (!parent.subtasks.find((c: any) => normalizeTaskSegment(c.taskNumber ?? '') === normTaskNumber)) {
        parent.subtasks.push(node);
      }
    }
    return node;
  };

  for (const t of tasks) {
    const path = Array.isArray(t.path) ? t.path : [];
    if (path.length === 0) continue;
    const node = ensureNode(path);
    const taskTypes = normalizeTaskTypes(t.taskType);
    const splitTaskTypes = taskTypes.filter(taskType => taskType !== 'TASK_FRAME' && taskType !== 'DEFAULT');

    node._id = t.id;
    node.taskType = splitTaskTypes.length > 1 ? 'TASK_FRAME' : (splitTaskTypes[0] ?? taskTypes[0] ?? 'DEFAULT');
    node.description = t.description || '';
    node.descriptionBoundingBoxes = (t.boundingBoxes || []).map(toBoundingBoxEntity);
    if (t.masterSolutionContent) node.masterSolutionContent = t.masterSolutionContent;
    if (t.gradingCriteria) node.gradingCriteria = t.gradingCriteria;
    // Persist the TOTAL on `maxPoints` (build-exam-tasks → ExamTask.declaredMaxPoints): the FIXED declared
    // total if the teacher pinned one, else the dynamic sum. For an answerable parent that also has its OWN
    // points, carry own on `ownMaxPoints` → ExamTask.gradingScheme.maxPoints.
    const total = typeof t.declaredMaxPoints === 'number' ? t.declaredMaxPoints : computeTaskTotal(t, tasks);
    // 0 means "no points assigned" → don't transport a 0 (build would drop it anyway; keep the node clean).
    if (typeof total === 'number' && total > 0) node.maxPoints = total;
    if (t.needsAnswer !== false && typeof t.ownPoints === 'number' && t.ownPoints > 0) {
      node.ownMaxPoints = t.ownPoints;
    }
    if (Array.isArray(t.additionalInfoIds) && t.additionalInfoIds.length > 0)
      node.additionalInfoIds = t.additionalInfoIds;

    if (splitTaskTypes.length > 1) {
      for (const taskType of splitTaskTypes) {
        const childNode = ensureNode([...path, taskType]);
        childNode._id = `${t.id}-${taskType}`;
        childNode.taskType = taskType;
        childNode.description = '';
        childNode.descriptionBoundingBoxes = [];
      }
    }
  }

  const roots: any[] = [];
  for (const [key, node] of normalizedNodeMap.entries()) {
    if (!key.includes('|')) roots.push(node);
  }

  roots.sort((a, b) => {
    const na = Number(String(a.taskNumber || '').replace(/[^0-9]/g, '')) || 0;
    const nb = Number(String(b.taskNumber || '').replace(/[^0-9]/g, '')) || 0;
    return na - nb;
  });

  return roots;
}
